import type { GaugeSegments } from "./TriColorGauge.tsx";

/* ── Heart Rate Timeline Data ── */

// prettier-ignore
export const DASH_HR = [
  88, 91, 86, 94, 98, 95, 102, 107, 103, 112, 116, 110, 120, 126, 121, 132, 138, 130, 142, 148, 140,
  152, 158, 150, 162, 168, 160, 172, 176, 170, 164, 158, 152, 148, 142, 138, 132, 128, 124, 118,
];

export const TIMELINE_SHOTS = [
  { id: "ts01", timeIdx: 3, made: true, zone: "paint" },
  { id: "ts02", timeIdx: 5, made: true, zone: "restricted" },
  { id: "ts03", timeIdx: 8, made: false, zone: "mid", error: "short" },
  { id: "ts04", timeIdx: 11, made: true, zone: "3pt" },
  { id: "ts05", timeIdx: 14, made: false, zone: "3pt", error: "left" },
  { id: "ts06", timeIdx: 17, made: true, zone: "paint" },
  { id: "ts07", timeIdx: 20, made: true, zone: "mid" },
  { id: "ts08", timeIdx: 22, made: true, zone: "restricted" },
  { id: "ts09", timeIdx: 25, made: false, zone: "3pt", error: "flat" },
  { id: "ts10", timeIdx: 27, made: true, zone: "mid" },
  { id: "ts11", timeIdx: 29, made: true, zone: "paint" },
  { id: "ts12", timeIdx: 31, made: false, zone: "deep", error: "short" },
  { id: "ts13", timeIdx: 33, made: true, zone: "3pt" },
  { id: "ts14", timeIdx: 35, made: true, zone: "mid" },
  { id: "ts15", timeIdx: 37, made: false, zone: "deep", error: "left" },
];

interface MemoTag {
  key: string;
  type: "zone" | "result" | "error";
}

export const TIMELINE_MEMOS: { timeIdx: number; lineIdx: number; tags: MemoTag[] }[] = [
  {
    timeIdx: 4,
    lineIdx: 0,
    tags: [
      { key: "3pt", type: "zone" },
      { key: "made", type: "result" },
    ],
  },
  {
    timeIdx: 9,
    lineIdx: 1,
    tags: [
      { key: "mid", type: "zone" },
      { key: "short", type: "error" },
    ],
  },
  {
    timeIdx: 15,
    lineIdx: 2,
    tags: [
      { key: "paint", type: "zone" },
      { key: "made", type: "result" },
    ],
  },
  {
    timeIdx: 18,
    lineIdx: 3,
    tags: [
      { key: "3pt", type: "zone" },
      { key: "left", type: "error" },
    ],
  },
  {
    timeIdx: 23,
    lineIdx: 4,
    tags: [
      { key: "mid", type: "zone" },
      { key: "made", type: "result" },
    ],
  },
  {
    timeIdx: 28,
    lineIdx: 5,
    tags: [
      { key: "3pt", type: "zone" },
      { key: "short", type: "error" },
    ],
  },
  {
    timeIdx: 32,
    lineIdx: 6,
    tags: [
      { key: "mid", type: "zone" },
      { key: "flat", type: "error" },
    ],
  },
  {
    timeIdx: 38,
    lineIdx: 7,
    tags: [
      { key: "paint", type: "zone" },
      { key: "made", type: "result" },
    ],
  },
];

/* ── Zone & Tag Meta ── */

export const ZONE_META: Record<string, { letter: string; color: string }> = {
  restricted: { letter: "R", color: "#ff3b30" },
  paint: { letter: "P", color: "#ff9500" },
  mid: { letter: "M", color: "#34c759" },
  "3pt": { letter: "3", color: "#0071e3" },
  deep: { letter: "D", color: "#6366f1" },
};

export const TAG_META: Record<string, { label: string; color: string }> = {
  restricted: { label: "R.A.", color: "#ff3b30" },
  paint: { label: "Paint", color: "#ff9500" },
  mid: { label: "Mid", color: "#34c759" },
  "3pt": { label: "3PT", color: "#0071e3" },
  deep: { label: "Deep", color: "#6366f1" },
  made: { label: "\u2713 Made", color: "#30d158" },
  short: { label: "\u2193 Short", color: "#ff9f0a" },
  left: { label: "\u2190 Left", color: "#bf5af2" },
  flat: { label: "\u2322 Flat", color: "#64d2ff" },
};

/* ── Insight Gauges ── */

export interface InsightGauge {
  id: string;
  segments: GaugeSegments;
  leftLabel: string;
  rightLabel: string;
  trend: string;
  trendType: "positive" | "negative";
}

export const INSIGHT_GAUGES: InsightGauge[] = [
  {
    id: "ig-dir",
    segments: { ext1: 5, warn1: 15, ok: 55, warn2: 15, ext2: 10 },
    leftLabel: "L",
    rightLabel: "R",
    trend: "+2%",
    trendType: "positive",
  },
  {
    id: "ig-arc",
    segments: { ext1: 8, warn1: 17, ok: 40, warn2: 20, ext2: 15 },
    leftLabel: "Flat",
    rightLabel: "High",
    trend: "+5%",
    trendType: "positive",
  },
  {
    id: "ig-depth",
    segments: { ext1: 7, warn1: 18, ok: 45, warn2: 18, ext2: 12 },
    leftLabel: "Short",
    rightLabel: "Over",
    trend: "-3%",
    trendType: "negative",
  },
];

export const HR_ZONE_FG = [
  { zone: "Zone 2", bpm: "100-120", pct: 72, color: "#34c759" },
  { zone: "Zone 3", bpm: "120-140", pct: 65, color: "#34c759" },
  { zone: "Zone 4", bpm: "140-160", pct: 48, color: "#ffcc00" },
  { zone: "Zone 5", bpm: "160+", pct: 31, color: "#ff3b30" },
];

export const AREA_PERF = [
  { area: "Paint", fg: 85, avgHr: 118, hrColor: "#34c759", barColor: "#ff9500" },
  { area: "Mid", fg: 58, avgHr: 142, hrColor: "#ffcc00", barColor: "#34c759" },
  { area: "3PT", fg: 34, avgHr: 155, hrColor: "#ff9500", barColor: "#0071e3" },
  { area: "Deep", fg: 18, avgHr: 168, hrColor: "#ff3b30", barColor: "#6366f1" },
];

/* ── Session Cards (for accumulation animation) ── */

// prettier-ignore
export const SESSION_CARDS = [
  { id: "s1", num: 1, shots: 487, fg: 54 },
  { id: "s2", num: 2, shots: 523, fg: 52 },
  { id: "s3", num: 3, shots: 441, fg: 56 },
  { id: "s4", num: 4, shots: 510, fg: 53 },
  { id: "s5", num: 5, shots: 468, fg: 55 },
  { id: "s6", num: 6, shots: 552, fg: 58 },
  { id: "s7", num: 7, shots: 495, fg: 54 },
  { id: "s8", num: 8, shots: 478, fg: 57 },
  { id: "s9", num: 9, shots: 531, fg: 56 },
  { id: "s10", num: 10, shots: 463, fg: 59 },
  { id: "s11", num: 11, shots: 544, fg: 55 },
  { id: "s12", num: 12, shots: 508, fg: 58 },
];

/* ── Accumulation Tiers (session → month → quarter → year → decade) ── */

export interface TierCard {
  label: string;
  sub: string;
  fg: number;
}

export interface TierGauge {
  value: number;
  trend: string;
}

export interface AccTier {
  id: string;
  summaryLabel: string;
  summaryCount: number;
  summaryUnit: string;
  summaryFg: number;
  /** HR zone FG% improvement: Zone2 / Zone3 / Zone4 / Zone5 */
  hrZones: [number, number, number, number];
  /** Direction / Arc / Depth gauges — improving over time */
  gauges: { direction: TierGauge; arc: TierGauge; depth: TierGauge };
  /** Card size scale factor — grows with each tier */
  cardScale: number;
  cards: TierCard[];
}

export const ACCUMULATION_TIERS: AccTier[] = [
  {
    id: "month",
    summaryLabel: "1 Month",
    summaryCount: 12,
    summaryUnit: "Sessions · 6,000 shots",
    summaryFg: 54,
    hrZones: [72, 65, 48, 31],
    gauges: {
      direction: { value: 55, trend: "+2%" },
      arc: { value: 40, trend: "+5%" },
      depth: { value: 45, trend: "-3%" },
    },
    cardScale: 0.85,
    cards: [], // Tier 0 uses SESSION_CARDS directly
  },
  {
    id: "quarter",
    summaryLabel: "3 Months",
    summaryCount: 38,
    summaryUnit: "Sessions · 19,000 shots",
    summaryFg: 58,
    hrZones: [75, 68, 54, 38],
    gauges: {
      direction: { value: 62, trend: "+7%" },
      arc: { value: 52, trend: "+12%" },
      depth: { value: 55, trend: "+10%" },
    },
    cardScale: 0.92,
    cards: [
      { label: "Month 1", sub: "6,000", fg: 54 },
      { label: "Month 2", sub: "6,300", fg: 56 },
      { label: "Month 3", sub: "6,700", fg: 58 },
    ],
  },
  {
    id: "year",
    summaryLabel: "1 Year",
    summaryCount: 152,
    summaryUnit: "Sessions · 76,000 shots",
    summaryFg: 63,
    hrZones: [80, 74, 62, 48],
    gauges: {
      direction: { value: 74, trend: "+19%" },
      arc: { value: 68, trend: "+28%" },
      depth: { value: 70, trend: "+25%" },
    },
    cardScale: 1.0,
    cards: [
      { label: "Q1", sub: "19K", fg: 58 },
      { label: "Q2", sub: "20K", fg: 60 },
      { label: "Q3", sub: "21K", fg: 62 },
      { label: "Q4", sub: "22K", fg: 63 },
    ],
  },
  {
    id: "decade",
    summaryLabel: "10 Years",
    summaryCount: 1520,
    summaryUnit: "Sessions · 760,000 shots",
    summaryFg: 72,
    hrZones: [88, 82, 74, 62],
    gauges: {
      direction: { value: 88, trend: "+33%" },
      arc: { value: 82, trend: "+42%" },
      depth: { value: 85, trend: "+40%" },
    },
    cardScale: 1.1,
    cards: Array.from({ length: 10 }, (_, i) => ({
      label: `Year ${i + 1}`,
      sub: `${76 + i * 3}K`,
      fg: 63 + i,
    })),
  },
];

/* ── SVG Helpers ── */

export const SVG_W = 600;
export const SVG_H = 130;
export const PAD = { l: 36, r: 12, t: 10, b: 20 };
export const GW = SVG_W - PAD.l - PAD.r;
export const GH = SVG_H - PAD.t - PAD.b;
const D_HR_MIN = 80;
const D_HR_MAX = 180;

export function dHrY(hr: number): number {
  return PAD.t + GH * (1 - (hr - D_HR_MIN) / (D_HR_MAX - D_HR_MIN));
}

export function dTimeX(i: number): number {
  return PAD.l + (i / (DASH_HR.length - 1)) * GW;
}

function buildBezierPath(
  data: number[],
  toX: (i: number) => number,
  toY: (v: number) => number,
): string {
  const pts = data.map((v, i) => ({ x: toX(i), y: toY(v) }));
  if (pts.length < 2) return "";
  const d: string[] = [`M ${pts[0].x.toFixed(1)} ${pts[0].y.toFixed(1)}`];
  const tension = 0.3;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[Math.max(0, i - 1)];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[Math.min(pts.length - 1, i + 2)];
    const cp1x = p1.x + ((p2.x - p0.x) * tension) / 3;
    const cp1y = p1.y + ((p2.y - p0.y) * tension) / 3;
    const cp2x = p2.x - ((p3.x - p1.x) * tension) / 3;
    const cp2y = p2.y - ((p3.y - p1.y) * tension) / 3;
    d.push(
      `C ${cp1x.toFixed(1)} ${cp1y.toFixed(1)}, ${cp2x.toFixed(1)} ${cp2y.toFixed(1)}, ${p2.x.toFixed(1)} ${p2.y.toFixed(1)}`,
    );
  }
  return d.join(" ");
}

export const DASH_HR_PATH = buildBezierPath(DASH_HR, dTimeX, dHrY);

export const DASH_HR_AREA = (() => {
  const lastX = dTimeX(DASH_HR.length - 1).toFixed(1);
  const firstX = dTimeX(0).toFixed(1);
  const botY = (PAD.t + GH).toFixed(1);
  return `${DASH_HR_PATH} L ${lastX} ${botY} L ${firstX} ${botY} Z`;
})();

export const HR_ZONE_BANDS = [
  { min: 80, max: 120, color: "rgba(52, 199, 89, 0.06)" },
  { min: 120, max: 140, color: "rgba(255, 204, 0, 0.06)" },
  { min: 140, max: 160, color: "rgba(255, 149, 0, 0.08)" },
  { min: 160, max: 180, color: "rgba(255, 59, 48, 0.10)" },
];

export function buildCosinePath(w: number, h: number): string {
  const points: string[] = [];
  const steps = 100;
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const x = t * w;
    const amp = 40 + t * 60;
    const y = h / 2 + Math.cos(t * Math.PI * 3) * amp;
    points.push(`${i === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`);
  }
  return points.join(" ");
}
